import { where } from "firebase/firestore";
import { isFaxUnread } from "@/common/faxStatus";
import { subscribeToCol, waitForEventsAuth } from "@/composables/events/handler";
import i18n from "@/i18n";

const FAX_COLLECTION = "fax-messages";

// Terminal statuses that are worth notifying the user about, per direction.
const OUTBOUND_NOTIFY_STATUSES = new Set(["sent", "delivered", "failed"]);
const INBOUND_NOTIFY_STATUSES = new Set(["received", "failed"]);

let dispatchFn = null;
let notifyFn = null;
let setUnreadCountFn = null;

// Tracks the last status seen per fax id so we only notify on real transitions
// (Firestore replays every matching doc on (re)subscribe).
const lastStatusById = new Map();

// Unread inbound fax ids, derived from the same snapshot stream.
const unreadIds = new Set();

/**
 * @param {Object} deps
 * @param {Function} deps.dispatch - namespaced dispatch for the faxes store
 * @param {Function} deps.notify - pushes a global alert ({ type, message })
 * @param {Function} [deps.setUnreadCount] - receives the live unread inbound count
 */
export function init({ dispatch, notify, setUnreadCount }) {
	dispatchFn = dispatch;
	notifyFn = notify;
	setUnreadCountFn = setUnreadCount ?? null;
}

export async function subscribe(organizationId) {
	if (!organizationId) {
		return;
	}

	// The rule compares resource.organizationId to the auth token claim, so the
	// listener must not start before Firebase Auth is signed in.
	await waitForEventsAuth();

	console.log("[FAX-EVENTS] Subscribing to organization:", organizationId);

	// Reset transition tracking so a fresh subscription treats the current
	// snapshot as the baseline instead of replaying old notifications.
	lastStatusById.clear();
	unreadIds.clear();
	publishUnreadCount();

	try {
		// Equality-only filter: adding orderBy would require a composite index.
		subscribeToCol(FAX_COLLECTION, [
			where("organizationId", "==", organizationId)
		], {
			onInitialLoad: primeBaseline,
			onAdded: handleFaxEvent,
			onUpdated: handleFaxEvent,
			onRemoved: handleFaxRemoved,
			onError: (error) => {
				console.error("[FAX-EVENTS] Subscription error:", {
					code: error.code,
					message: error.message
				});
			}
		});
	} catch (err) {
		console.error("[FAX-EVENTS] Failed to subscribe:", err);
		throw err;
	}
}

function primeBaseline(snapshot) {
	snapshot?.docs?.forEach(doc => {
		const fax = doc.data();

		lastStatusById.set(doc.id, fax.status);
		trackUnread(doc.id, fax);
	});

	publishUnreadCount();
	// No refresh here: the fax view fetches its own data when opened.
}

function trackUnread(id, fax) {
	if (isFaxUnread(fax)) {
		unreadIds.add(id);
	} else {
		unreadIds.delete(id);
	}
}

function publishUnreadCount() {
	setUnreadCountFn?.(unreadIds.size);
}

function handleFaxRemoved(_fax, id) {
	lastStatusById.delete(id);

	if (unreadIds.delete(id)) {
		publishUnreadCount();
	}

	dispatchFn("onFaxEvent");
}

function handleFaxEvent(fax, id) {
	if (!fax) {
		return;
	}

	const previousStatus = lastStatusById.get(id);

	lastStatusById.set(id, fax.status);
	trackUnread(id, fax);
	publishUnreadCount();

	// Keep the list in sync on every change.
	dispatchFn("onFaxEvent");

	if (previousStatus === fax.status) {
		return;
	}

	notifyStatusChange(fax);
}

function notifyStatusChange(fax) {
	if (!notifyFn) {
		return;
	}

	const isInbound = fax.direction === "inbound";
	const shouldNotify = isInbound
		? INBOUND_NOTIFY_STATUSES.has(fax.status)
		: OUTBOUND_NOTIFY_STATUSES.has(fax.status);

	if (!shouldNotify) {
		return;
	}

	if (fax.status === "failed") {
		notifyFn({
			type: "error",
			message: i18n.t("fax-notification.failed", {
				contact: isInbound ? fax.from : fax.to
			})
		});

		return;
	}

	if (isInbound) {
		notifyFn({
			type: "info",
			message: i18n.t("fax-notification.received", { contact: fax.from })
		});

		return;
	}

	notifyFn({
		type: "success",
		message: i18n.t(`fax-notification.${fax.status}`, { contact: fax.to })
	});
}
